/**
 * The scenario workspace header — the one strip that is the same in Review and
 * Edit mode, pinned under the Sizers tab strip.
 *
 *   ← All scenarios
 *   [Untitled scenario ✎]          [Review | Edit inputs] [⟲ v3 current ▾] [⋯] [Save as deal]
 *   RTL · Arrow Point · v3 priced Oct 9, 1:20 PM · ● Active · (2 unpriced changes)
 *
 * It absorbs what used to be spread over the drilldown's Scenario card and the
 * sizer's resumed-scenario banner: the name is edited in place (no Save name
 * button), the version history is a dropdown, Archive / raw payload / copy link
 * live in the ⋯ menu, and Save as deal moved here off the fixed footer.
 *
 * A BLANK sizer (no persisted scenario yet) gets the plain title + subtitle and
 * the Save as deal button — nothing else here applies until it has priced.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Popover from "@mui/material/Popover";
import Tooltip from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import ArchiveOutlined from "@mui/icons-material/ArchiveOutlined";
import ChevronLeftRounded from "@mui/icons-material/ChevronLeftRounded";
import CodeRounded from "@mui/icons-material/CodeRounded";
import ContentCopyRounded from "@mui/icons-material/ContentCopyRounded";
import EditOutlined from "@mui/icons-material/EditOutlined";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import HistoryRounded from "@mui/icons-material/HistoryRounded";
import LinkRounded from "@mui/icons-material/LinkRounded";
import MoreHorizRounded from "@mui/icons-material/MoreHorizRounded";
import OpenInNewRounded from "@mui/icons-material/OpenInNewRounded";
import SaveOutlined from "@mui/icons-material/SaveOutlined";
import UnarchiveOutlined from "@mui/icons-material/UnarchiveOutlined";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";
import CircularProgress from "@mui/material/CircularProgress";
import { useSnackbar } from "notistack";

import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import { slate, status as STATUS, teal } from "../../../theme/colors";
import { sizerApi } from "../../../api/sizerApi";
import { formatDateTime } from "../../../_src_shared/utils/functions/formatDateTime";
import { SZ } from "../sizerUi";
import {
  errText,
  fmtDrift,
  versionInputLine,
  versionRows,
} from "../scenarioModel";
import { WORKSPACE_MODES } from "./workspaceModel";
import {
  NUM,
  ScenarioStatusPill,
  TypeTag,
  WsPrimaryButton,
  WsSecondaryButton,
} from "./workspaceParts";

const LIST_PATH = "/sizers/scenarios";

const shortWho = (email) => (email ? String(email).split("@")[0] : null);

const Dot = () => (
  <Box component="span" sx={{ color: SZ.ink400, px: "2px" }}>
    ·
  </Box>
);

/* ============================================================ Name */

/**
 * The title IS the name input. Dashed underline + pencil at rest, a field
 * outline on hover, a focus ring while typing. Saves on Enter or blur (only on
 * a real change), Esc reverts. Naming is metadata — it never touches a priced
 * version, and the toast says so.
 */
function ScenarioName({ scenarioId, doc, onDocChange }) {
  const { enqueueSnackbar } = useSnackbar();
  const [name, setName] = useState(doc?.name || "");
  const saved = useRef(doc?.name || "");
  const inputRef = useRef(null);

  useEffect(() => {
    setName(doc?.name || "");
    saved.current = doc?.name || "";
  }, [doc?.name]);

  const commit = () => {
    const next = name.trim();
    if (next === saved.current) return;
    sizerApi
      .updateScenario(scenarioId, { name: next })
      .then(() => {
        saved.current = next;
        onDocChange?.({ ...(doc || {}), name: next });
        enqueueSnackbar("Name saved — doesn't touch any priced version", {
          variant: "success",
        });
      })
      .catch((e) => {
        setName(saved.current);
        enqueueSnackbar(errText(e, "Could not save the name"), {
          variant: "error",
        });
      });
  };

  return (
    <Box
      sx={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        maxWidth: "100%",
        borderRadius: "7px",
        "&:hover .name-input": {
          boxShadow: `0 0 0 1px ${SZ.lineStrong}`,
          bgcolor: SZ.card,
        },
        "&:focus-within .name-pencil": { display: "none" },
      }}
    >
      <Box
        component="input"
        ref={inputRef}
        className="name-input"
        value={name}
        placeholder="Untitled scenario"
        aria-label="Scenario name"
        onChange={(e) => setName(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
          if (e.key === "Escape") {
            setName(saved.current);
            setTimeout(() => inputRef.current?.blur(), 0);
          }
        }}
        sx={{
          fieldSizing: "content",
          minWidth: 120,
          maxWidth: 440,
          font: "inherit",
          fontSize: 21,
          fontWeight: 700,
          letterSpacing: "-0.01em",
          color: name ? SZ.ink900 : SZ.ink400,
          fontStyle: name ? "normal" : "italic",
          bgcolor: "transparent",
          border: 0,
          borderRadius: "6px 6px 0 0",
          px: "4px",
          ml: "-4px",
          py: "1px",
          outline: "none",
          transition: "box-shadow .12s, background .12s",
          "&::placeholder": { color: SZ.ink400, fontStyle: "italic" },
          "&:focus": {
            boxShadow: `0 0 0 2px ${SZ.teal500}`,
            bgcolor: SZ.card,
            borderRadius: "6px",
          },
        }}
      />
      <Tooltip title={name ? "Rename" : "Add a name"}>
        <Box
          component="button"
          type="button"
          className="name-pencil"
          aria-label={name ? "Rename" : "Add a name"}
          onClick={() => inputRef.current?.focus()}
          sx={{
            display: "inline-flex",
            border: 0,
            background: "none",
            p: "2px",
            cursor: "pointer",
            color: SZ.ink900,
            "& svg": { fontSize: 16 },
          }}
        >
          <EditOutlined />
        </Box>
      </Tooltip>
    </Box>
  );
}

/* ============================================================ Versions */

/**
 * "⟲ v3 current ▾" — every version, newest first, with when / who / the inputs
 * that moved and its headline figure + drift vs v1. Turns amber ("older") when
 * an earlier version is on screen. Picking one loads its inputs AND its stored
 * result in either mode.
 */
function VersionPicker({ doc, shownVersion, onLoadVersion }) {
  const [anchor, setAnchor] = useState(null);
  const { rows } = versionRows(doc);
  const current = doc?.currentVersion;
  const older =
    shownVersion != null && current != null && shownVersion !== current;
  const type = doc?.scenarioType;

  return (
    <>
      <WsSecondaryButton
        onClick={(e) => setAnchor(e.currentTarget)}
        startIcon={<HistoryRounded />}
        sx={
          older
            ? {
                borderColor: "#F5DDB0",
                bgcolor: STATUS.warning.bg,
                color: STATUS.warning.fg,
              }
            : null
        }
      >
        <Box
          component="span"
          sx={{
            fontFamily: SZ.mono,
            fontWeight: 600,
            color: older ? STATUS.warning.fg : SZ.teal700,
          }}
        >
          v{shownVersion ?? current ?? "—"}
        </Box>
        <Box
          component="span"
          sx={{ fontWeight: 500, color: older ? STATUS.warning.fg : SZ.ink500 }}
        >
          {older ? "older" : "current"}
        </Box>
        <ExpandMoreRounded sx={{ ml: -0.5 }} />
      </WsSecondaryButton>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.75,
              width: 420,
              maxWidth: "calc(100vw - 32px)",
              borderRadius: "10px",
              border: `1px solid ${SZ.line}`,
              boxShadow: "0 12px 32px rgba(16,28,34,.14)",
            },
          },
        }}
      >
        <Box sx={{ p: 0.75, maxHeight: 420, overflowY: "auto" }}>
          {rows.map((v) => {
            const on = v.version === (shownVersion ?? current);
            const drift = v.version === 1 ? 0 : v.driftBps;
            return (
              <Box
                key={v.version}
                component="button"
                type="button"
                onClick={() => {
                  setAnchor(null);
                  if (!on) onLoadVersion?.(v);
                }}
                sx={{
                  width: "100%",
                  display: "grid",
                  gridTemplateColumns: "58px minmax(0,1fr) auto",
                  gap: 1.5,
                  alignItems: "center",
                  textAlign: "left",
                  border: 0,
                  borderRadius: "8px",
                  p: "10px 12px",
                  font: "inherit",
                  cursor: "pointer",
                  bgcolor: on ? SZ.teal50 : "transparent",
                  "&:hover": { bgcolor: on ? SZ.teal50 : SZ.bg },
                }}
              >
                <Box>
                  <Txt
                    component="div"
                    sx={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: SZ.ink900,
                      ...NUM,
                    }}
                  >
                    v{v.version}
                  </Txt>
                  {v.version === current && (
                    <Txt
                      component="div"
                      sx={{ fontSize: 10.5, fontWeight: 600, color: SZ.good }}
                    >
                      current
                    </Txt>
                  )}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Txt
                    component="div"
                    sx={{
                      fontSize: 12,
                      color: SZ.ink500,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {[
                      formatDateTime(v.pricedAt, "MMM d, h:mm a"),
                      shortWho(v.pricedBy),
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </Txt>
                  <Txt
                    component="div"
                    sx={{
                      fontSize: 12,
                      color: SZ.ink500,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {versionInputLine(type, v.scenario) || "—"}
                  </Txt>
                </Box>
                <Box sx={{ textAlign: "right" }}>
                  <Txt
                    component="div"
                    sx={{
                      fontFamily: SZ.mono,
                      fontSize: 13.5,
                      fontWeight: 600,
                      color: SZ.ink900,
                      ...NUM,
                    }}
                  >
                    {v.primary || "—"}
                  </Txt>
                  <Txt
                    component="div"
                    sx={{
                      fontFamily: SZ.mono,
                      fontSize: 11.5,
                      ...NUM,
                      color:
                        drift > 0 ? SZ.bad : drift < 0 ? SZ.good : SZ.ink400,
                    }}
                  >
                    {fmtDrift(drift)}
                  </Txt>
                </Box>
              </Box>
            );
          })}
          {!rows.length && (
            <Txt
              component="div"
              sx={{ p: 1.5, fontSize: 12.5, color: SZ.ink400 }}
            >
              No versions recorded on this scenario.
            </Txt>
          )}
        </Box>
        <Box
          sx={{
            borderTop: `1px solid ${SZ.line}`,
            p: "9px 14px",
            fontSize: 11.5,
            color: SZ.ink400,
          }}
        >
          Versions are immutable. Drift is measured against v1.
        </Box>
      </Popover>
    </>
  );
}

/* ============================================================ ⋯ menu */

function RawPayloadDialog({ open, onClose, title, value }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <Flx jb ac sx={{ p: "16px 20px", borderBottom: `1px solid ${SZ.line}` }}>
        <Txt sx={{ fontSize: 16, fontWeight: 600, color: SZ.ink900 }}>
          {title}
        </Txt>
        <Button variant="text" onClick={onClose}>
          Close
        </Button>
      </Flx>
      <Box
        component="pre"
        sx={{
          m: 0,
          p: 2.5,
          maxHeight: "70vh",
          overflow: "auto",
          fontFamily: SZ.mono,
          fontSize: 11.5,
          lineHeight: 1.55,
          color: SZ.ink700,
          bgcolor: "#fbfcfc",
        }}
      >
        {JSON.stringify(value || {}, null, 2)}
      </Box>
    </Dialog>
  );
}

function MoreMenu({
  scenarioId,
  doc,
  onDocChange,
  onDuplicate,
  shownScenario,
  shownVersion,
}) {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [anchor, setAnchor] = useState(null);
  const [rawOpen, setRawOpen] = useState(false);
  const archived = doc?.status === "Archived";
  const converted = doc?.status === "Converted" || Boolean(doc?.dealId);

  const setStatus = (status, undoTo) =>
    sizerApi
      .updateScenario(scenarioId, { status })
      .then(() => {
        onDocChange?.({ ...(doc || {}), status });
        enqueueSnackbar(
          status === "Archived"
            ? "Scenario archived — history kept"
            : "Scenario reactivated",
          {
            variant: status === "Archived" ? "default" : "success",
            autoHideDuration: undoTo ? 8000 : undefined,
            action: undoTo
              ? (key) => (
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => {
                      closeSnackbar(key);
                      setStatus(undoTo);
                    }}
                    sx={{ fontWeight: 700, color: "inherit" }}
                  >
                    Undo
                  </Button>
                )
              : undefined,
          },
        );
      })
      .catch((e) =>
        enqueueSnackbar(errText(e, "Update failed"), { variant: "error" }),
      );

  const items = [
    {
      key: "link",
      icon: <LinkRounded />,
      label: "Copy link",
      onClick: () =>
        navigator.clipboard
          ?.writeText(window.location.href)
          .then(() => enqueueSnackbar("Link copied", { variant: "success" }))
          .catch(() =>
            enqueueSnackbar("Could not copy the link", { variant: "error" }),
          ),
    },
    {
      key: "dup",
      icon: <ContentCopyRounded />,
      label: "Duplicate as new scenario",
      onClick: onDuplicate,
      hidden: !onDuplicate,
    },
    {
      key: "raw",
      icon: <CodeRounded />,
      label: "View raw payload",
      onClick: () => setRawOpen(true),
    },
    archived
      ? {
          key: "status",
          icon: <UnarchiveOutlined />,
          label: "Reactivate",
          onClick: () => setStatus("Active"),
        }
      : {
          key: "status",
          icon: <ArchiveOutlined />,
          label: "Archive",
          danger: true,
          disabled: converted,
          reason: "A converted scenario stays with its loan",
          onClick: () => setStatus("Archived", doc?.status || "Active"),
        },
  ].filter((i) => !i.hidden);

  return (
    <>
      <Tooltip title="More actions">
        <span>
          <WsSecondaryButton
            onClick={(e) => setAnchor(e.currentTarget)}
            sx={{ width: 40, px: 0, justifyContent: "center" }}
          >
            <MoreHorizRounded />
          </WsSecondaryButton>
        </span>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { minWidth: 230, mt: 0.75 } } }}
      >
        {items.map((it) => (
          <MenuItem
            key={it.key}
            disabled={it.disabled}
            onClick={() => {
              setAnchor(null);
              it.onClick?.();
            }}
            sx={{
              fontSize: 13.5,
              color: it.danger ? STATUS.error.fg : SZ.ink900,
            }}
          >
            <ListItemIcon
              sx={{ color: "inherit", minWidth: 30, "& svg": { fontSize: 18 } }}
            >
              {it.icon}
            </ListItemIcon>
            <ListItemText
              primary={it.label}
              secondary={it.disabled ? it.reason : null}
              primaryTypographyProps={{
                sx: { fontSize: 13.5, fontWeight: it.danger ? 600 : 400 },
              }}
              secondaryTypographyProps={{ sx: { fontSize: 11.5 } }}
            />
          </MenuItem>
        ))}
      </Menu>
      <RawPayloadDialog
        open={rawOpen}
        onClose={() => setRawOpen(false)}
        title={`Scenario payload${shownVersion ? ` · v${shownVersion}` : ""}`}
        value={shownScenario || doc?.scenario}
      />
    </>
  );
}

/* ============================================================ Save */

function SaveAsDeal({ doc, onSave, savePending, disabledReason }) {
  const navigate = useNavigate();
  if (doc?.dealId) {
    return (
      <WsPrimaryButton
        onClick={() => navigate(`/loan/${doc.dealId}`)}
        startIcon={<OpenInNewRounded />}
      >
        Open loan
      </WsPrimaryButton>
    );
  }
  // A sizer with no save-as-deal flow (DSCR, today) passes no `onSave`.
  if (!onSave) return null;
  const btn = (
    <WsPrimaryButton
      onClick={onSave}
      disabled={Boolean(disabledReason) || savePending}
      startIcon={
        savePending ? (
          <CircularProgress size={15} sx={{ color: "#fff" }} />
        ) : (
          <SaveOutlined />
        )
      }
    >
      {savePending ? "Saving…" : "Save as deal"}
    </WsPrimaryButton>
  );
  return disabledReason ? (
    <Tooltip title={disabledReason}>
      <span>{btn}</span>
    </Tooltip>
  ) : (
    btn
  );
}

/* ============================================================ Mode tabs */

/**
 * Results | Inputs — a segmented pill on its own row under the meta line: a
 * grey track (#eef3f5, 1px #e3e9ec border — the house segmented style) with
 * the selected segment as a raised white pill. The Results segment carries the
 * headline figure ("8.125%") as a small mono teal chip.
 *
 *   rest    500 · slate-500 · transparent
 *   active  600 · slate-900 · white + soft shadow
 */
const ModeTab = ({ on, label, sub, subTone, onClick }) => (
  <Box
    component="button"
    type="button"
    role="tab"
    aria-selected={on}
    onClick={onClick}
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      border: 0,
      font: "inherit",
      fontSize: 13.5,
      lineHeight: "20px",
      fontWeight: on ? 600 : 500,
      color: on ? slate[900] : slate[500],
      bgcolor: on ? "#fff" : "transparent",
      boxShadow: on
        ? "0 1px 2px rgba(15,23,42,.12), 0 0 0 1px rgba(15,23,42,.04)"
        : "none",
      padding: "6px 18px",
      borderRadius: "7px",
      cursor: on ? "default" : "pointer",
      transition: "color .12s, background .12s, box-shadow .12s",
      "&:hover": on ? {} : { color: slate[700] },
      "&:focus-visible": {
        outline: `2px solid ${teal[300]}`,
        outlineOffset: "1px",
      },
    }}
  >
    {label}
    {sub && (
      <Box
        component="span"
        sx={{
          fontFamily: SZ.mono,
          fontSize: 11,
          fontWeight: 500,
          lineHeight: "18px",
          px: "6px",
          borderRadius: "4px",
          color: subTone === "warn" ? STATUS.warning.fg : teal[700],
          bgcolor: subTone === "warn" ? STATUS.warning.bg : teal[50],
          ...NUM,
        }}
      >
        {sub}
      </Box>
    )}
  </Box>
);

function ModeTabs({
  mode,
  onModeChange,
  resultsSub,
  changesCount = 0,
  shownVersion,
}) {
  return (
    <Flx ac jb g={2} sx={{ flexWrap: "wrap" }}>
      <Box
        role="tablist"
        aria-label="Scenario view"
        sx={{
          display: "inline-flex",
          gap: "2px",
          mt: "14px",
          mb: 2,
          p: "4px",
          bgcolor: "#eef3f5",
          border: "1px solid #e3e9ec",
          borderRadius: "10px",
        }}
      >
        <ModeTab
          on={mode === WORKSPACE_MODES.REVIEW}
          label="Results"
          sub={resultsSub}
          onClick={() => onModeChange?.(WORKSPACE_MODES.REVIEW)}
        />
        <ModeTab
          on={mode === WORKSPACE_MODES.EDIT}
          label="Inputs"
          // How many inputs differ from the version on screen — visible from
          // either view, so unpriced edits are never out of sight.
          sub={changesCount > 0 ? String(changesCount) : null}
          subTone="warn"
          onClick={() => onModeChange?.(WORKSPACE_MODES.EDIT)}
        />
      </Box>
      {changesCount > 0 && shownVersion != null && (
        <Flx ac g={0.75} sx={{ mt: "14px", mb: 2, color: STATUS.warning.fg }}>
          <WarningAmberRounded sx={{ fontSize: 16 }} />
          <Txt sx={{ fontSize: 12.5, fontWeight: 500 }}>
            Results still show v{shownVersion}
          </Txt>
        </Flx>
      )}
    </Flx>
  );
}

/* ============================================================ Header */

export default function ScenarioWorkspaceHeader({
  // Blank sizer
  title,
  subtitle,
  // Persisted scenario
  scenarioId,
  doc,
  onDocChange,
  typeLabel,
  shownVersion,
  shownScenario,
  onLoadVersion,
  mode,
  onModeChange,
  changesCount = 0,
  // "8.500%" — the headline figure the Results segment carries.
  resultsSub,
  onDuplicate,
  // Both
  onSave,
  savePending,
  saveDisabledReason,
  // "Admin tools" menu (SizerDevBar variant="menu"), beside the version picker.
  adminTools,
}) {
  const navigate = useNavigate();

  if (!scenarioId) {
    return (
      <Flx
        jb
        g={2}
        sx={{ alignItems: "flex-start", flexWrap: "wrap", py: 2.5 }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Txt
            component="div"
            sx={{
              fontSize: 21,
              fontWeight: 700,
              letterSpacing: "-0.01em",
              color: SZ.ink900,
              lineHeight: 1.2,
            }}
          >
            {title}
          </Txt>
          {subtitle && (
            <Txt
              component="div"
              sx={{ fontSize: 13, color: SZ.ink500, mt: "3px" }}
            >
              {subtitle}
            </Txt>
          )}
        </Box>
        <Flx ac g={1}>
          {adminTools}
          <SaveAsDeal
            onSave={onSave}
            savePending={savePending}
            disabledReason={saveDisabledReason}
          />
        </Flx>
      </Flx>
    );
  }

  const shownRun = (doc?.versions || []).find(
    (v) => v.version === shownVersion,
  );
  const pricedAt = shownRun?.pricedAt || doc?.updatedAt;

  return (
    <Box sx={{ pt: 2 }}>
      {/* The right-hand buttons centre on the WHOLE left block — back link,
          title and meta line together. */}
      <Flx jb g={2} sx={{ alignItems: "center", flexWrap: "wrap" }}>
        <Box sx={{ minWidth: 0, flex: "1 1 320px" }}>
          <Box>
            <Box
              component="button"
              type="button"
              onClick={() => navigate(LIST_PATH)}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.25,
                border: 0,
                background: "none",
                p: 0,
                mb: 1.5,
                ml: "-4px",
                font: "inherit",
                fontSize: 12.5,
                color: SZ.ink500,
                cursor: "pointer",
                "&:hover": { color: SZ.teal700 },
                "& svg": { fontSize: 17 },
              }}
            >
              <ChevronLeftRounded />
              All scenarios
            </Box>
          </Box>

          <ScenarioName
            scenarioId={scenarioId}
            doc={doc}
            onDocChange={onDocChange}
          />
          <Flx
            ac
            g={0.75}
            sx={{
              mt: 0.75,
              flexWrap: "wrap",
              fontSize: 12.5,
              color: SZ.ink500,
            }}
          >
            {typeLabel && <TypeTag>{typeLabel}</TypeTag>}
            {doc?.createdByCompany && (
              <>
                <span>{doc.createdByCompany}</span>
                <Dot />
              </>
            )}
            {shownVersion != null && (
              <span>
                v{shownVersion} priced{" "}
                {formatDateTime(pricedAt, "MMM d, h:mm a") || "—"}
              </span>
            )}
            {doc?.status && <ScenarioStatusPill status={doc.status} />}
          </Flx>
        </Box>
        {/* Right column: the scenario-level buttons (version, ⋯, Save as
            deal). The Results / Inputs tabs sit on their own row below. */}
        <Flx column g={1.25} sx={{ alignItems: "flex-end" }}>
          <Flx ac g={1} sx={{ flexWrap: "wrap", justifyContent: "flex-end" }}>
            {adminTools}
            <VersionPicker
              doc={doc}
              shownVersion={shownVersion}
              onLoadVersion={onLoadVersion}
            />
            <MoreMenu
              scenarioId={scenarioId}
              doc={doc}
              onDocChange={onDocChange}
              onDuplicate={onDuplicate}
              shownScenario={shownScenario}
              shownVersion={shownVersion}
            />
            <SaveAsDeal
              doc={doc}
              onSave={onSave}
              savePending={savePending}
              disabledReason={saveDisabledReason}
            />
          </Flx>
        </Flx>
      </Flx>
      <ModeTabs
        mode={mode}
        onModeChange={onModeChange}
        resultsSub={resultsSub}
        changesCount={changesCount}
        shownVersion={shownVersion}
      />
    </Box>
  );
}

/**
 * Under the header when an EARLIER version is on screen. Still a resume, never
 * a rollback: pricing from v1's inputs appends v(current+1).
 */
export function OlderVersionBanner({
  doc,
  shownVersion,
  onBackToCurrent,
  onEditFrom,
}) {
  const current = doc?.currentVersion;
  if (shownVersion == null || current == null || shownVersion === current)
    return null;
  const run = (doc?.versions || []).find((v) => v.version === shownVersion);
  return (
    <Flx
      ac
      jb
      g={1.5}
      sx={{
        flexWrap: "wrap",
        mb: 2.5,
        p: "10px 14px",
        borderRadius: SZ.radiusSm,
        border: "1px solid #F5DDB0",
        bgcolor: STATUS.warning.bg,
        color: STATUS.warning.fg,
        fontSize: 13,
      }}
    >
      <span>
        You're looking at <strong>v{shownVersion}</strong>
        {run?.pricedAt
          ? ` (${formatDateTime(run.pricedAt, "MMM d, h:mm a")})`
          : ""}
        . The current version is v{current}.
      </span>
      <Flx ac g={1}>
        <WsSecondaryButton
          onClick={onBackToCurrent}
          sx={{ height: 32, minHeight: 32 }}
        >
          Back to current
        </WsSecondaryButton>
        {onEditFrom && (
          <WsPrimaryButton
            onClick={onEditFrom}
            sx={{ height: 32, minHeight: 32 }}
          >
            Edit from v{shownVersion}
          </WsPrimaryButton>
        )}
      </Flx>
    </Flx>
  );
}
